/**
 * InterviewPrepHistory.jsx — RC 1.52 Slice 3A
 *
 * Read-only library list of standalone Interview Prep documents for
 * the Documents library. Mirror of `CoverLettersHistory.jsx`.
 *
 *   - browse the list (GET /api/interview-prep)
 *   - preview content in a read-only modal using
 *     `InterviewPrepViewer` (same 5-bucket render as the workspace)
 *   - delete a document (DELETE /api/interview-prep/{id}) via the
 *     shared `DeleteDocumentDialog` (kind="interview_prep").
 *
 * No export. No edit. No regenerate. Per operator instruction, PDF /
 * DOCX export for Interview Prep lives in a future slice (3A.3).
 *
 * Generation surface lives exclusively inside the Job Application
 * Workspace (Interview Prep section). This page is library only.
 */

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Sparkles, Loader2, Trash2, ExternalLink, Lock, Brain,
} from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { cachedFetch, peek, invalidate, KEYS } from '@/lib/listCache';
import { toast } from 'sonner';
import DeleteDocumentDialog from '@/components/DeleteDocumentDialog';
import InterviewPrepViewer from '@/components/interview/InterviewPrepViewer';

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(); } catch { return ''; }
}

function PreviewModal({ open, onOpenChange, id, title, subtitle }) {
  const [interviewPrep, setInterviewPrep] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !id) return;
    let alive = true;
    setLoading(true); setError(''); setInterviewPrep(null);
    // Use the header-gated canonical envelope so we receive
    // `result.interview_prep` consistently with the workspace lane.
    apiFetch(`/api/interview-prep/${id}`, {
      headers: { 'X-Envelope': 'rc_1_52_canonical' },
    })
      .then((d) => {
        if (!alive) return;
        // Canonical envelope: { result: { interview_prep: {...} } }.
        // Legacy passthrough (no header) would have given us the bare
        // row with `content_json.interview_prep`. We send the header
        // so canonical is the expected shape, but we accept both
        // defensively.
        const payload =
          d?.result?.interview_prep
          ?? d?.content_json?.interview_prep
          ?? null;
        setInterviewPrep(payload);
      })
      .catch((e) => alive && setError(e?.message || 'Failed to load.'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [open, id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[80vh] overflow-y-auto"
        data-testid="interview-prep-preview-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {title}
            <Badge variant="outline" className="text-[10px] tracking-[0.16em] uppercase border-ndor-edge text-ndor-ink-muted">
              <Lock className="h-2.5 w-2.5 mr-1" />
              Read-only
            </Badge>
          </DialogTitle>
          {subtitle && <p className="text-xs text-ndor-ink-muted">{subtitle}</p>}
        </DialogHeader>
        {loading && <Loader2 className="h-5 w-5 animate-spin text-ndor-ink-muted mx-auto my-6" />}
        {error && <p className="text-sm text-ndor-error">{error}</p>}
        {!loading && !error && (
          <div data-testid="interview-prep-preview-body">
            <InterviewPrepViewer interviewPrep={interviewPrep} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// RC 1.59 Slice 2 — Documents tab cache adoption (Tier 1).
const INTERVIEW_PREP_TTL_MS = 5 * 60_000;

export default function InterviewPrepHistory() {
  const [items, setItems] = useState(() => {
    const seed = peek(KEYS.interviewPrepAll(), INTERVIEW_PREP_TTL_MS);
    return seed.hit && Array.isArray(seed.data) ? seed.data : [];
  });
  const [loading, setLoading] = useState(() => {
    const seed = peek(KEYS.interviewPrepAll(), INTERVIEW_PREP_TTL_MS);
    return !seed.hit;
  });
  const [previewId, setPreviewId] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewSubtitle, setPreviewSubtitle] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const list = await cachedFetch(
        KEYS.interviewPrepAll(),
        () => apiFetch('/api/interview-prep'),
        { ttlMs: INTERVIEW_PREP_TTL_MS },
      );
      setItems(Array.isArray(list) ? list : []);
    } catch (err) {
      toast.error(err?.message || 'Failed to load interview prep.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-ndor-ink-muted" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="interview-prep-history">
      {items.length === 0 ? (
        <Card className="border-ndor-edge shadow-sm">
          <CardContent className="p-8 text-center space-y-3">
            <div className="mx-auto w-12 h-12 rounded-full bg-ndor-surface-alt flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-ndor-ink-muted" />
            </div>
            <p className="text-sm font-medium text-ndor-ink">No interview prep yet</p>
            <p className="text-xs text-ndor-ink-muted leading-relaxed max-w-sm mx-auto">
              Interview prep is generated when you apply to a job. Open a
              job and use the Interview Prep step in the application flow.
            </p>
            <Button asChild variant="outline" size="sm" data-testid="interview-prep-history-jobs-link">
              <Link to="/jobs"><ExternalLink className="h-3.5 w-3.5 mr-1.5" />Browse jobs</Link>
            </Button>

            <div
              className="mt-5 mx-auto max-w-md rounded-md border border-ndor-edge bg-ndor-surface-alt/40 p-3 text-left"
              data-testid="interview-prep-empty-reviewer-hint"
            >
              <p className="text-[10px] font-mono-ui uppercase tracking-[0.16em] text-ndor-ink-muted">
                Recommended next step
              </p>
              <ol className="mt-2 space-y-1.5 text-xs text-ndor-ink-secondary leading-relaxed">
                <li className="flex items-start gap-2">
                  <span className="font-medium text-ndor-ink">1.</span>
                  <span><Link to="/jobs" className="text-ndor-info hover:underline">Import or choose a job</Link> from the Jobs page.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-medium text-ndor-ink">2.</span>
                  <span>Run <span className="font-medium text-ndor-ink">Analyse Fit</span> and generate a tailored CV for the role.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-medium text-ndor-ink">3.</span>
                  <span>Inside the application flow, generate <span className="font-medium text-ndor-ink">Interview Prep</span> — it lands here in your library.</span>
                </li>
              </ol>
            </div>
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-2">
          {items.map((ip) => (
            <li
              key={ip.id}
              /* RC 1.59 Slice 4 Mobile pass — stack on phones; resume
                 horizontal layout at sm: (≥640 px). */
              className="rounded-lg border border-ndor-edge bg-ndor-surface px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-3"
              data-testid={`interview-prep-row-${ip.id}`}
            >
              <div className="flex items-start gap-3 sm:contents min-w-0 w-full">
                <div className="w-9 h-9 rounded-lg bg-ndor-surface-alt flex items-center justify-center shrink-0">
                  <Brain className="h-4 w-4 text-ndor-ink-muted" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-ndor-ink truncate" data-testid="interview-prep-row-title">
                    {ip.title || 'Untitled interview prep'}
                  </p>
                  <p className="text-xs text-ndor-ink-muted line-clamp-2 sm:line-clamp-none break-words">
                    {ip.target_role || ip.source_filename || 'No target role'} · Generated {fmtDate(ip.created_at)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 sm:shrink-0 self-end sm:self-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPreviewId(ip.id);
                    setPreviewTitle(ip.title || 'Interview prep');
                    setPreviewSubtitle(`Generated ${fmtDate(ip.created_at)}`);
                  }}
                  data-testid="interview-prep-row-preview"
                >
                  Preview
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-ndor-ink-muted hover:text-ndor-error"
                  onClick={() => setDeleteTarget(ip)}
                  data-testid="interview-prep-row-delete"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="text-[11px] text-ndor-ink-muted leading-relaxed pt-1">
        To generate new interview prep, open a job from{' '}
        <Link to="/jobs" className="underline hover:text-ndor-ink">/jobs</Link> and use
        the application flow.
      </p>

      <PreviewModal
        open={!!previewId}
        onOpenChange={(v) => !v && setPreviewId(null)}
        id={previewId}
        title={previewTitle}
        subtitle={previewSubtitle}
      />
      <DeleteDocumentDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        kind="interview_prep"
        doc={deleteTarget}
        onDeleted={() => {
          invalidate(KEYS.interviewPrepAll());
          setDeleteTarget(null);
          refresh();
        }}
      />
    </div>
  );
}
